import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, runTransaction, updateDoc, deleteField, serverTimestamp, getDoc } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { isPatientFemale } from '../../../utils/conditionChecks';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';
import { rerenderCoverPageIfStale } from '../../../utils/autoCoverPage';
import { buildCIExclusionsContext } from '../../../utils/clinicalInterviewWorkflow';
import { useAuth } from '../../../hooks/useAuth';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { computeValidCodes, computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { finalizeSectionReview } from '../../../utils/sectionReviewFinalization';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { toast } from 'react-toastify';
import { SparklesIcon, CheckIcon } from '@heroicons/react/solid';
import { renderModelInput } from '../../../utils/renderModelInput';
import {
  emitParagraphApproval,
  emitParagraphEdited,
} from '../../../analytics/reportEvents';
import { useAnthropicComparison } from '../../../hooks/useAnthropicComparison';
import { CompareModeToolbar } from './components/CompareModeToolbar';
import { CompareClaudePanel } from './components/CompareClaudePanel';

// Detect macOS so keyboard shortcut labels show ⌘ instead of Ctrl.
// navigator.platform is deprecated but still the most reliable synchronous
// check across all major browsers; userAgentData is async and not universal.
const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const modKey = isMac ? '⌘' : 'Ctrl';

// ---------------------------------------------------------------------------
// BulkApproveModal
// ---------------------------------------------------------------------------
// End-of-review dialog shown when the clinician has walked through every
// paragraph but some remain unapproved (i.e. they were skipped or scrolled
// past). Presents three options so the clinician can choose their own path:
//   1. Approve all remaining in one click (fast, for when they're confident).
//   2. Go back and review the unapproved ones individually.
//   3. Save partial progress and exit.
// ---------------------------------------------------------------------------
function BulkApproveModal({ unapprovedCount, onApproveAll, onGoBack, onSaveAndExit }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Review Complete</h2>
        <p className="text-gray-600 mb-6">
          You have{' '}
          <span className="font-bold text-red-600">{unapprovedCount}</span>{' '}
          unapproved paragraph{unapprovedCount !== 1 ? 's' : ''} remaining.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={onApproveAll}
            className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <CheckIcon className="w-5 h-5" />
            Approve All Remaining
          </button>
          <button
            onClick={onGoBack}
            className="w-full px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-500 font-semibold transition-colors"
          >
            Go Back &amp; Review
          </button>
          <button
            onClick={onSaveAndExit}
            className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold border border-gray-300 transition-colors"
          >
            Save &amp; Exit
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KeyboardShortcutHint
// ---------------------------------------------------------------------------
// A small floating pill at the bottom of the viewport that shows the active
// keyboard shortcuts. It adapts to the current mode (normal vs focus) so the
// clinician always knows which keys are available without memorising them.
// Slides out of view when no paragraph is selected.
// ---------------------------------------------------------------------------
function KeyboardShortcutHint({ visible, inFocusMode }) {
  return (
    <div
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-[55]
        bg-gray-900/90 text-white px-6 py-2.5 rounded-full shadow-lg
        text-sm font-medium flex items-center gap-4 backdrop-blur-sm
        transition-all duration-300
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
      `}
    >
      {inFocusMode ? (
        <>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">{modKey}+↵</kbd> Approve
          </span>
          <span className="text-gray-500">|</span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">Esc</kbd> Cancel
          </span>
        </>
      ) : (
        <>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">↵</kbd> Approve
          </span>
          <span className="text-gray-500">|</span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">↓</kbd> Skip
          </span>
          <span className="text-gray-500">|</span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">R</kbd> Review
          </span>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// ReviewDocumentCIv2  –  Keyboard-First Review UX
// ===========================================================================
// This is the v2 prototype of the Clinical Interview review page. The key
// differences from v1 (RD_CI.js) are:
//
// 1. **Full-width document** – no persistent side panel. AI inputs only appear
//    inline when entering "focus mode" (review/edit).
//
// 2. **Keyboard shortcuts** – Enter (approve), Down-arrow (skip), R (review).
//    Designed so clinicians can fly through paragraphs without clicking.
//
// 3. **Left-border status lines** – each paragraph has a coloured left border:
//    Red = unseen, Amber/yellow = scrolled-past-but-unapproved, Green = approved.
//    The active paragraph gets a thicker + glowing border.
//
// 4. **Focus mode** – pressing R or clicking the ✨ sparkle icon darkens the
//    page, scrolls the paragraph to the top, makes it immediately editable,
//    and slides in the AI inputs below it.
//
// 5. **Bulk approve** – when the clinician reaches the end with unapproved
//    paragraphs still remaining, a modal offers "Approve All Remaining",
//    "Go Back & Review", or "Save & Exit".
//
// All Firestore logic (approval writes, edit history, exit flow) is kept
// identical to v1 so both pages are data-compatible.
// ===========================================================================
export default function ReviewDocumentCIv2() {

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const organisationName = claims?.organisation || 'default';

  // Module constants – identical to v1 so Firestore paths match.
  const MODULE_NUMBER = 1;
  const MODULE_PREFIX_CODE = "ci";
  const MODULE_TITLE = "Clinical Interview";

  const patientIsFemale = useMemo(() => {
    return isPatientFemale(patientDataModel);
  }, [patientDataModel]);

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // -----------------------------------------------------------------------
  // docStructure – mirrors v1 exactly so the same paragraph codes are used.
  // Each section maps a sequential key to { title, code } where `code` is
  // the Firestore field key inside the report outputs object.
  // -----------------------------------------------------------------------
  const docStructure = useMemo(() => {
    const baseStructure = {
      "Presenting Complaint": {
        "1": { "title": "Referral Information", "code": "ci_COM_referral_information" },
        "2": { "title": "History of Presenting Complaint", "code": "ci_COM_history_of_presenting_complaint" },
        "3": { "title": "Impact on Daily Functioning", "code": "ci_COM_impact_on_daily_functioning" },
      },
      "Developmental History": {
        "1": { "title": "Pre-natal and Birth", "code": "ci_DEV_pre_natal_and_birth" },
        "2": { "title": "Early Years", "code": "ci_DEV_early_years" },
        "3": { "title": "Childhood and Adolescence", "code": "ci_DEV_childhood_and_adolescence" },
        "4": { "title": "Childhood Living Environment", "code": "ci_DEV_childhood_living_environment" },
        "5": { "title": "Education", "code": "ci_DEV_education" },
      },
      "Personal and Social History": {
        "1": { "title": "Current Living Situation", "code": "ci_DEV_current_living_situation" },
        "2": { "title": "Relationships", "code": "ci_DEV_relationships" },
        "3": { "title": "Employment", "code": "ci_DEV_employment" },
      },
      "Lifestyle History": {
        "1": { "title": "Lifestyle Factors", "code": "ci_LIF_lifestyle_factors" },
        "2": { "title": "Alcohol and Substance Use", "code": "ci_LIF_alcohol_and_substance_use" },
        "3": { "title": "Habits", "code": "ci_LIF_habits" },
        "4": { "title": "Driving", "code": "ci_LIF_driving" },
      },
      "Forensic History": {
        "1": { "title": "History of Legal Issues", "code": "ci_FOR_history_of_legal_issues" },
        "2": { "title": "History as a Perpetrator", "code": "ci_FOR_perpetrator_history" },
        "3": { "title": "History as a Victim", "code": "ci_FOR_victim_history" },
      },
      "Family History": {
        "1": { "title": "Family Health Conditions", "code": "ci_FAM_health_conditions" },
      },
      "Medical History": {
        "1": { "title": "Health Conditions", "code": "ci_MED_health_conditions" },
        "2": { "title": "Hospital Visits", "code": "ci_MED_hospital_visits" },
        "3": { "title": "Medications", "code": "ci_MED_medications" },
        "4": { "title": "Allergies", "code": "ci_MED_allergies" },
        "5": { "title": "Vision and Hearing", "code": "ci_MED_vision_and_hearing" },
      },
      "Psychiatric History": {
        "1": { "title": "Previous and Current Diagnoses", "code": "ci_PSY_previous_and_current_diagnoses" },
        "2": { "title": "Previous and Current Treatments", "code": "ci_PSY_previous_and_current_treatments" },
        "3": { "title": "Trauma and Risk History", "code": "ci_PSY_trauma_and_risk_history" },
      },
      "Mental State Examination": {
        "1": { "title": "Summary", "code": "ci_MSE_summary" },
      },
      "Risk Assessment": {
        "1": { "title": "Summary", "code": "ci_RSK_summary" },
      },
      "Formulation and Provisional Diagnosis": {
        "1": { "title": "Summary", "code": "ci_DF" },
      },
    };

    if (patientIsFemale) {
      baseStructure["Medical History"]["6"] = { "title": "Women's Health", "code": "ci_MED_womens_health" };
    }

    const { excludedTitles } = buildCIExclusionsContext(patientDataModel, organisationName);
    if (excludedTitles && excludedTitles.includes('Mental Status Examination')) {
      delete baseStructure["Mental State Examination"];
    }

    return baseStructure;
  }, [patientIsFemale, patientDataModel, organisationName]);

  // Report paragraph text keyed by code (e.g. ci_COM_referral_information → "…").
  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------

  // Which paragraph code the clinician has selected ("active"). Null = none.
  const [selectedCode, setSelectedCode] = useState(null);

  // True when the clinician is in focus/review mode (paragraph is editable,
  // overlay is active, AI inputs are shown inline below the paragraph).
  const [focusMode, setFocusMode] = useState(false);

  // Set of paragraph codes that have been approved (persisted in Firestore).
  const [reviewedCodes, setReviewedCodes] = useState(new Set());

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // Set of paragraph codes that have been scrolled into view at least once.
  // Used to transition the left-border from red (unseen) → amber (seen but
  // unapproved).
  const [seenCodes, setSeenCodes] = useState(new Set());

  const [totalParagraphs, setTotalParagraphs] = useState(0);

  // Content captured at the moment the clinician enters focus mode. Used to
  // revert edits if they press Escape (cancel).
  const [preEditContent, setPreEditContent] = useState({});

  // The AI model inputs fetched from Firestore (clinician notes, patient
  // prefill, informant prefill, etc.) keyed by paragraph code.
  const [modelInputsFirestore, setModelInputsFirestore] = useState(null);

  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // -----------------------------------------------------------------------
  // Anthropic comparison (dev/staging UAT only)
  // -----------------------------------------------------------------------
  const ciSections = useMemo(() => Object.keys(docStructure), [docStructure]);
  const {
    isAvailable: isCompareAvailable,
    isLoading: isCompareLoading,
    hasComparison,
    showClaude,
    sideBySide,
    claudeOutputs,
    progress: compareProgress,
    triggerComparison,
    setViewMode,
    toggleSideBySide,
    getDisplayedOutput,
  } = useAnthropicComparison({
    patientId,
    reportStage: MODULE_TITLE,
    reportSections: ciSections,
    conditions: [],
    orgId: patientDataModel?.orgId,
    organisationName: claims?.organisation,
  });

  // -----------------------------------------------------------------------
  // Refs
  // -----------------------------------------------------------------------
  const documentContainerRef = useRef(null);
  const observerRef = useRef(null);

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  // The AI model inputs for the currently active paragraph. Derived on the
  // fly so we don't need to sync a separate piece of state when the active
  // paragraph changes.
  const activeInputs = useMemo(() => {
    if (!modelInputsFirestore || !selectedCode) return null;
    return modelInputsFirestore[selectedCode] || null;
  }, [modelInputsFirestore, selectedCode]);

  // -----------------------------------------------------------------------
  // Effects – data loading
  // -----------------------------------------------------------------------

  // Count paragraphs that actually have generated content.
  useEffect(() => {
    const total = Object.values(docStructure).reduce((acc, section) => {
      const available = Object.values(section).filter(
        ({ code }) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
      );
      return acc + available.length;
    }, 0);
    setTotalParagraphs(total);
  }, [docStructure, REPORT_DATA_OUTPUTS]);

  // Pull the model inputs object from the patient data model. These are
  // the clinician notes, decisions, patient prefills, etc. that the AI used
  // to generate each paragraph.
  useEffect(() => {
    if (patientDataModel) {
      const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
      if (inputs) {
        setModelInputsFirestore(inputs);
      }
    }
  }, [patientDataModel]);

  // Load previously approved paragraphs from Firestore on mount so the UI
  // shows the correct green borders and progress bar for returning users.
  useEffect(() => {
    const loadApprovedParagraphs = async () => {
      try {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const approved = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
          // Intersect persisted approvals with the currently valid codes so
          // orphaned approval keys (paragraphs later blanked/deleted) cannot
          // inflate reviewedCodes above totalParagraphs and corrupt the gate.
          // This mirrors useApprovalTracking's intersection (the v1 path).
          const validCodes = computeValidCodes(docStructure, REPORT_DATA_OUTPUTS);
          setReviewedCodes(new Set(Object.keys(approved).filter(code => validCodes.has(code))));
        }
      } catch (error) {
        console.error('Error loading approved paragraphs:', error);
      }
    };
    loadApprovedParagraphs();
    // Re-run when validity inputs change so the intersection stays accurate
    // (mirrors useApprovalTracking, which re-derives on validCodes changes).
  }, [patientId, docStructure, REPORT_DATA_OUTPUTS]);

  // Lock body scroll while on this page (same as v1).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Toggle the "purple highlighted sections" warning bar based on whether
  // any paragraphs remain unapproved.
  useEffect(() => {
    setShowWarning(totalParagraphs > reviewedCodes.size);
  }, [reviewedCodes.size, totalParagraphs]);

  // -----------------------------------------------------------------------
  // IntersectionObserver – scroll tracking
  // -----------------------------------------------------------------------
  // Watches every [data-paragraph-container] element within the scrollable
  // document container. When a paragraph scrolls into view (>30% visible),
  // its code is added to `seenCodes`. This drives the red → amber left-
  // border transition: red means the clinician hasn't even scrolled past
  // the paragraph yet, amber means they've seen it but haven't approved.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const container = documentContainerRef.current;
    if (!container || totalParagraphs === 0) return;

    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.target.id) {
              setSeenCodes(prev => {
                if (prev.has(entry.target.id)) return prev;
                const next = new Set(prev);
                next.add(entry.target.id);
                return next;
              });
            }
          });
        },
        { root: container, threshold: 0.3 }
      );

      container.querySelectorAll('[data-paragraph-container]').forEach(el => observer.observe(el));
      observerRef.current = observer;
    }, 200);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [totalParagraphs]);

  // -----------------------------------------------------------------------
  // Helper – left-border classes for a paragraph
  // -----------------------------------------------------------------------
  // Returns Tailwind classes that control the left-border colour (status)
  // and width (active vs. inactive). The active paragraph also gets a
  // subtle glow animation defined in ReviewDocuments.css.
  // -----------------------------------------------------------------------
  const getBorderClasses = useCallback((code) => {
    const isActive = selectedCode === code;
    const isApproved = reviewedCodes.has(code);
    const wasSeen = seenCodes.has(code);

    const width = isActive ? 'border-l-[6px]' : 'border-l-4';

    let color;
    if (isApproved) {
      color = 'border-l-green-500';
    } else if (wasSeen) {
      color = 'border-l-amber-400';
    } else {
      color = 'border-l-red-400';
    }

    // The glow animation only runs when the paragraph is the active one
    // and we're NOT in focus mode (in focus mode the white card shadow
    // already provides strong emphasis).
    const pulse = isActive && !focusMode ? 'v2-paragraph-active' : '';

    return `${width} ${color} ${pulse}`;
  }, [selectedCode, reviewedCodes, seenCodes, focusMode]);

  // Collect every paragraph code that has content (used by bulk approve).
  const getAllParagraphCodes = useCallback(() => {
    const codes = [];
    Object.values(docStructure).forEach(section => {
      Object.values(section).forEach(({ code }) => {
        if (REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
          codes.push(code);
        }
      });
    });
    return codes;
  }, [docStructure, REPORT_DATA_OUTPUTS]);

  // -----------------------------------------------------------------------
  // Paragraph selection (non-focus-mode click)
  // -----------------------------------------------------------------------
  const handleParagraphClick = useCallback((code) => {
    if (focusMode) return;
    setSelectedCode(code);
  }, [focusMode]);

  // Deselect when clicking empty space inside the document container.
  const handleDocumentClick = useCallback((event) => {
    if (focusMode) return;
    if (!event.target.closest('[data-paragraph-container]')) {
      setSelectedCode(null);
    }
  }, [focusMode]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) container.addEventListener('click', handleDocumentClick);
    return () => {
      if (container) container.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  // -----------------------------------------------------------------------
  // Navigation – scroll to a paragraph and make it active
  // -----------------------------------------------------------------------
  const selectAndScrollTo = useCallback((code) => {
    setSelectedCode(code);
    setTimeout(() => {
      const el = document.getElementById(code);
      if (el && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const scrollTop =
          documentContainerRef.current.scrollTop +
          (elRect.top - containerRect.top) -
          containerRect.height / 3;
        documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }
    }, 50);
  }, []);

  // -----------------------------------------------------------------------
  // Focus mode – enter
  // -----------------------------------------------------------------------
  // Entering focus mode means:
  //  • A dark overlay fades in over the header/chrome; non-active elements dim.
  //  • The active paragraph scrolls to the top and becomes a white card.
  //  • The paragraph's text becomes immediately contentEditable.
  //  • The AI inputs panel slides in below the paragraph.
  //  • Keyboard shortcuts switch to focus-mode bindings (Ctrl+Enter, Esc).
  //
  // `codeOverride` allows the sparkle icon click handler to specify which
  // paragraph to focus, since the selectedCode state may not have updated
  // yet when triggered from a click on a non-active paragraph.
  // -----------------------------------------------------------------------
  const enterFocusMode = useCallback((codeOverride) => {
    const code = codeOverride || selectedCode;
    if (!code || !modelInputsFirestore) return;

    setSelectedCode(code);
    setPreEditContent(prev => ({
      ...prev,
      [code]: REPORT_DATA_OUTPUTS[code] || ''
    }));
    setFocusMode(true);

    // After React re-renders with focusMode=true (paragraph becomes
    // contentEditable), scroll the paragraph to the top of the *container*
    // (not the page) so it lands below the fixed header, then place the
    // cursor at the end of its text.
    setTimeout(() => {
      const el = document.getElementById(code);
      const container = documentContainerRef.current;
      if (el && container) {
        // Use the wrapper div (direct parent) as the scroll target so the
        // section subtitle above the paragraph is also visible.
        const wrapper = el.parentElement;
        const target = wrapper || el;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 24;
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });

        setTimeout(() => {
          const p = el.querySelector('p');
          if (p) {
            p.focus();
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(p);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 350);
      }
    }, 50);
  }, [selectedCode, modelInputsFirestore, REPORT_DATA_OUTPUTS]);

  // -----------------------------------------------------------------------
  // Focus mode – cancel (Escape)
  // -----------------------------------------------------------------------
  // Reverts any edits the clinician made during this focus session and
  // exits back to the normal review mode. The paragraph stays selected
  // so they can immediately use keyboard shortcuts (Enter / Down / R).
  // -----------------------------------------------------------------------
  const exitFocusMode = useCallback(() => {
    if (selectedCode && preEditContent[selectedCode] !== undefined) {
      REPORT_DATA_OUTPUTS[selectedCode] = preEditContent[selectedCode];
      // React doesn't reliably update contentEditable children after user
      // modifications, so we patch the DOM directly to ensure the reverted
      // text is visible.
      setTimeout(() => {
        const el = document.querySelector(`[id="${selectedCode}"] p`);
        if (el) {
          el.textContent = decodeHtmlEntities(preEditContent[selectedCode]);
        }
      }, 0);
    }
    setFocusMode(false);
  }, [selectedCode, preEditContent, REPORT_DATA_OUTPUTS]);

  // -----------------------------------------------------------------------
  // Firestore – approve / un-approve a single paragraph
  // -----------------------------------------------------------------------
  // Identical to v1's handleParagraphApproval. Writes to the
  // `approvedParagraphs` map within the patient's report section doc.
  // -----------------------------------------------------------------------
  const handleParagraphApproval = useCallback(async (code, isApproving) => {
    try {
      const docRef = doc(db, 'patients', patientId);
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

      // Targeted per-key write (not a full-map read-modify-write transaction)
      // so rapid approvals on slow 3G/4G don't contend on the single patient
      // document's version — see RD_CI.js for the full rationale. Paragraph
      // codes are dot-free identifiers and the four field paths below are
      // siblings (none is an ancestor of another), so there is no conflicting
      // field-path rejection.
      const updateData = {
        [`${sectionPath}.approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Timed, PII-free confirmation log (updateDoc resolves on server ack).
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // Patient Reports finalising with missing content.
      throw error;
    }
  }, [patientId, REPORT_DATA_OUTPUTS, docStructure]);

  // -----------------------------------------------------------------------
  // Advance-to-next helper
  // -----------------------------------------------------------------------
  // After an approve or skip action, find the next unreviewed paragraph
  // below the current position. If there isn't one but unapproved ones
  // exist earlier in the document (skipped / scrolled past), show the
  // bulk-approve modal instead.
  // -----------------------------------------------------------------------
  const advanceToNext = useCallback((currentCode, updatedReviewedCodes) => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const paragraphArray = Array.from(allParagraphs);
    const currentIndex = paragraphArray.findIndex(p => p.id === currentCode);

    const nextUnreviewed = paragraphArray.find((p, index) =>
      index > currentIndex && !updatedReviewedCodes.has(p.id)
    );

    if (nextUnreviewed) {
      selectAndScrollTo(nextUnreviewed.id);
    } else {
      const hasUnapproved = paragraphArray.some(p => !updatedReviewedCodes.has(p.id));
      setSelectedCode(null);
      if (hasUnapproved) {
        setShowBulkApproveModal(true);
      }
    }
  }, [selectAndScrollTo]);

  // -----------------------------------------------------------------------
  // Quick approve (Enter key – no focus mode)
  // -----------------------------------------------------------------------
  // Approves the currently active paragraph without entering focus mode,
  // then advances to the next unreviewed paragraph.
  // -----------------------------------------------------------------------
  const handleQuickApprove = useCallback(() => {
    if (!selectedCode) return;

    const currentCode = selectedCode;

    // Fire-and-forget Firestore write with rollback on failure. Tracked so
    // Save & Exit awaits it before deriving the section approval flag.
    trackApproval(
      handleParagraphApproval(currentCode, true).catch(() => {
        // Roll back the optimistic update and tell the clinician — a silently
        // failed approval is exactly how incomplete sections used to slip
        // through to the Patient Report.
        setReviewedCodes(prev => {
          const next = new Set(prev);
          next.delete(currentCode);
          return next;
        });
        toast.error(
          'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
          { toastId: 'paragraph-approval-save-error' }
        );
      })
    );

    const newReviewedCodes = new Set([...reviewedCodes, currentCode]);
    setReviewedCodes(newReviewedCodes);

    advanceToNext(currentCode, newReviewedCodes);
  }, [selectedCode, reviewedCodes, handleParagraphApproval, advanceToNext]);

  // -----------------------------------------------------------------------
  // Skip (Down-arrow key)
  // -----------------------------------------------------------------------
  // Moves to the next unreviewed paragraph without approving the current
  // one. The current paragraph stays unapproved (its left border remains
  // amber once scrolled past).
  // -----------------------------------------------------------------------
  const handleSkip = useCallback(() => {
    if (!selectedCode) return;
    advanceToNext(selectedCode, reviewedCodes);
  }, [selectedCode, reviewedCodes, advanceToNext]);

  // -----------------------------------------------------------------------
  // Focus mode approve (Ctrl+Enter)
  // -----------------------------------------------------------------------
  // Saves any edits the clinician made while in focus mode AND approves the
  // paragraph in a single Firestore transaction (if content changed). Then
  // exits focus mode and advances to the next unreviewed paragraph.
  //
  // If the clinician didn't edit anything, falls back to a simple approval
  // transaction (same as quick-approve).
  // -----------------------------------------------------------------------
  const handleFocusModeApprove = useCallback(async () => {
    if (!selectedCode) return;

    const code = selectedCode;
    const currentContent = REPORT_DATA_OUTPUTS[code];
    const originalContent = preEditContent[code];
    const contentChanged = currentContent !== originalContent;

    try {
      if (contentChanged) {
        // Combined edit-save + approve in one transaction to minimise
        // Firestore round-trips and keep the data consistent.
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue: originalContent,
          newValue: currentContent,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) throw new Error("Document does not exist!");

          const data = docSnap.data();
          const currentApproved =
            data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
          const currentEditHistory =
            data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {};

          if (!currentEditHistory[code]) currentEditHistory[code] = {};
          currentEditHistory[code][timestampKey] = editEntry;

          currentApproved[code] = {
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser.uid,
            approvedByName: auth.currentUser.displayName,
            content: currentContent,
          };

          transaction.update(docRef, {
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApproved,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.editHistory`]: currentEditHistory,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.outputs`]: {
              ...data.dataModel?.['6_manageReports']?.sections?.[MODULE_NUMBER]?.outputs,
              [code]: currentContent,
            },
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName,
          });
        });

        // One transaction, two facts: the rewrite and the approval that came
        // with it. Both are mirrored here (codes and lengths only); the
        // unchanged branch below approves through handleParagraphApproval,
        // which emits its own approval, so neither path emits twice.
        emitParagraphEdited({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code,
          prevText: originalContent,
          priorHistory: REPORT_EDIT_HISTORY[code] || {},
          nextText: currentContent,
        });
        emitParagraphApproval({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code,
          assessmentType: 'adhd',
          isApproving: true,
        });
      } else {
        await handleParagraphApproval(code, true);
      }

      const newReviewedCodes = new Set([...reviewedCodes, code]);
      setReviewedCodes(newReviewedCodes);
      setSectionDirty(true);
      setFocusMode(false);

      advanceToNext(code, newReviewedCodes);
    } catch (error) {
      console.error('Focus mode approve failed:', error);
      toast.error(
        'The paragraph approval did not save. Please approve it again.',
        { toastId: 'paragraph-approval-save-error' }
      );
    }
  }, [selectedCode, REPORT_DATA_OUTPUTS, REPORT_EDIT_HISTORY, preEditContent, reviewedCodes, handleParagraphApproval, advanceToNext, patientId, docStructure]);

  // -----------------------------------------------------------------------
  // Bulk approve – approve every remaining unapproved paragraph at once
  // -----------------------------------------------------------------------
  const handleBulkApprove = useCallback(async () => {
    const allCodes = getAllParagraphCodes();
    const unapprovedCodes = allCodes.filter(code => !reviewedCodes.has(code));
    if (unapprovedCodes.length === 0) return;

    try {
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await transaction.get(docRef);
        if (!docSnap.exists()) throw new Error("Document does not exist!");

        const data = docSnap.data();
        const currentApproved =
          data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};

        unapprovedCodes.forEach(code => {
          currentApproved[code] = {
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser.uid,
            approvedByName: auth.currentUser.displayName,
            content: REPORT_DATA_OUTPUTS[code],
          };
        });

        transaction.update(docRef, {
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApproved,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName,
        });
      });

      // Bulk approval is many approvals in one write, so it mirrors as one
      // event per paragraph — the same rows an individual review would leave.
      unapprovedCodes.forEach((code) => {
        emitParagraphApproval({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code,
          assessmentType: 'adhd',
          isApproving: true,
        });
      });

      setReviewedCodes(new Set(allCodes));
      setSectionDirty(true);
      setShowBulkApproveModal(false);
    } catch (error) {
      console.error('Bulk approve failed:', error);
      toast.error(
        'The bulk approval did not save. Please try again.',
        { toastId: 'paragraph-approval-save-error' }
      );
    }
  }, [getAllParagraphCodes, reviewedCodes, patientId, REPORT_DATA_OUTPUTS, docStructure]);

  // Close the bulk-approve modal and jump back to the first unapproved
  // paragraph so the clinician can review individually.
  const handleBulkGoBack = useCallback(() => {
    setShowBulkApproveModal(false);
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnapproved = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));
    if (firstUnapproved) {
      selectAndScrollTo(firstUnapproved.id);
    }
  }, [reviewedCodes, selectAndScrollTo]);

  // -----------------------------------------------------------------------
  // Exit review – save approval state and navigate back to documents page
  // -----------------------------------------------------------------------
  // Identical to v1: writes the overall approved flag, triggers doc
  // generation when fully approved, re-renders stale reports, and
  // navigates away.
  // -----------------------------------------------------------------------
  const handleExitReview = useCallback(async () => {
    setIsSaving(true);
    try {
      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot, using the same shared logic as the on-screen
      // progress. This replaces the previous client-state-derived write.
      const { isFullyApproved: approvedNow, outstandingCodes } = await finalizeSectionReview({
        patientId,
        modulePrefix: MODULE_PREFIX_CODE,
        moduleNumber: MODULE_NUMBER,
        docStructure,
      });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when either:
      //   1. "Phantom approvals" exist — paragraphs shown green whose background
      //      write actually failed (rapid clicking can overload the per-document
      //      transaction queue), or
      //   2. the UI believed everything was approved but the snapshot found
      //      outstanding content (e.g. paragraphs generated after page load).
      // The affected paragraphs are reverted to needs-review and the patient
      // document is re-fetched so the page reflects reality.
      const uiClaimedComplete = totalParagraphs > 0 && reviewedCodes.size >= totalParagraphs;
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (uiClaimedComplete || unpersisted.length > 0)) {
        setReviewedCodes(prev => {
          const next = new Set(prev);
          unpersisted.forEach(code => next.delete(code));
          return next;
        });
        await fetchPatientData(patientId);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      const shouldRenderSection = approvedNow && sectionDirty;

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: approvedNow && !patientDataModel?.dataModel?.['6_manageReports']?.ciReportFirstApprovedAt,
      });

      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "Clinical Interview",
          clinicianId: auth.currentUser.uid,
          patientId: patientId,
        });
      }

      rerenderPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid, patientDataModel, organisation: organisationName })
        .catch((e) => console.error('rerenderPatientReportIfStale (CI v2) failed:', e));
      rerenderCoverPageIfStale({ patientId, clinicianId: auth.currentUser.uid, patientDataModel, organisation: organisationName })
        .catch((e) => console.error('rerenderCoverPageIfStale (CI v2) failed:', e));

      clearPatientData();
      setSectionDirty(false);
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  }, [sectionDirty, docStructure, patientDataModel, patientId, clearPatientData, navigate, organisationName, settlePendingApprovals, totalParagraphs, reviewedCodes, fetchPatientData]);

  // Header "Start / Continue Review" button handler.
  const goToFirstUnreviewed = useCallback(() => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));
    if (firstUnreviewed) selectAndScrollTo(firstUnreviewed.id);
  }, [reviewedCodes, selectAndScrollTo]);

  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // -----------------------------------------------------------------------
  // Keyboard shortcuts
  // -----------------------------------------------------------------------
  // A global keydown listener that routes keys to the appropriate handler
  // based on whether we're in focus mode or normal mode. Disabled while a
  // modal dialog is open.
  //
  // Normal mode:  Enter → approve, ArrowDown → skip, R → enter focus mode
  // Focus mode:   Ctrl/Cmd+Enter → approve (save edits), Escape → cancel
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showBulkApproveModal || showInitialWarning) return;

      if (focusMode) {
        if (e.key === 'Escape') {
          e.preventDefault();
          exitFocusMode();
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleFocusModeApprove();
        }
        return;
      }

      if (!selectedCode) return;

      // Don't intercept when the user is typing in a regular input field
      // (shouldn't normally happen on this page, but just in case).
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          handleQuickApprove();
          break;
        case 'ArrowDown':
          e.preventDefault();
          handleSkip();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          enterFocusMode();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    focusMode, selectedCode, showBulkApproveModal, showInitialWarning,
    exitFocusMode, handleFocusModeApprove, handleQuickApprove, handleSkip, enterFocusMode,
  ]);


  // -----------------------------------------------------------------------
  // Section component (inner)
  // -----------------------------------------------------------------------
  // Renders a report section heading (e.g. "Presenting Complaint") and its
  // child paragraphs. Each paragraph gets:
  //  • A left-border coloured by its review status.
  //  • A ✨ sparkle icon on the right to enter focus mode.
  //  • In focus mode: a white card wrapper + inline AI inputs below.
  // -----------------------------------------------------------------------
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2
          className={`text-2xl font-bold mb-4 text-blue-500 transition-opacity duration-300
            ${focusMode ? 'opacity-20' : ''}`}
        >
          {title}
        </h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = getDisplayedOutput(code, REPORT_DATA_OUTPUTS[code]);
          const decodedContent = decodeHtmlEntities(content);
          const isActive = selectedCode === code;

          return (
            <React.Fragment key={key}>
              {/* Paragraph wrapper – becomes a white card in focus mode */}
              <div
                className={`
                  mb-4 transition-all duration-300
                  ${focusMode && isActive ? 'bg-white rounded-xl shadow-2xl p-5' : ''}
                  ${focusMode && !isActive ? 'opacity-20 pointer-events-none' : ''}
                `}
              >
                {subTitle && (
                  <h3
                    className={`font-semibold mb-2 transition-colors duration-300
                      ${focusMode && isActive ? 'text-gray-900' : 'text-gray-900'}`}
                  >
                    {subTitle}
                  </h3>
                )}

                <div
                  data-paragraph-container
                  id={code}
                  className={`
                    relative text-sm font-medium p-3 rounded-r-lg
                    transition-all duration-300
                    ${getBorderClasses(code)}
                    ${isActive && !focusMode ? 'bg-violet-50/30 ring-1 ring-violet-300' : ''}
                    ${!isActive && !focusMode ? 'hover:bg-gray-50' : ''}
                    ${!focusMode ? 'cursor-pointer' : ''}
                  `}
                  onClick={() => !focusMode && handleParagraphClick(code)}
                >
                  <p
                    className="text-gray-700 leading-relaxed whitespace-pre-wrap pr-8"
                    contentEditable={focusMode && isActive && !showClaude}
                    onKeyDown={suppressFormattingShortcuts}
                    onInput={(e) => {
                      if (focusMode && isActive) {
                        const newContent = e.target.innerText;
                        if (REPORT_DATA_OUTPUTS) {
                          REPORT_DATA_OUTPUTS[code] = newContent;
                        }
                      }
                    }}
                    onPaste={handlePlainTextPaste}
                    suppressContentEditableWarning={true}
                  >
                    {decodedContent}
                  </p>

                  {/* Sparkle icon – shown on unapproved paragraphs when NOT in
                      focus mode. Clicking it enters focus mode for that paragraph. */}
                  {!focusMode && !reviewedCodes.has(code) && (
                    <button
                      className="absolute top-2 right-2 p-1 text-violet-300 hover:text-violet-600 transition-colors rounded-full hover:bg-violet-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        enterFocusMode(code);
                      }}
                      title="Review this paragraph (R)"
                    >
                      <SparklesIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* --------------------------------------------------------- */}
              {/* Inline AI inputs panel – slides in below the active        */}
              {/* paragraph when in focus mode. Contains the data the AI     */}
              {/* used to generate this paragraph plus action buttons.       */}
              {/* --------------------------------------------------------- */}
              {focusMode && isActive && activeInputs && (
                <div className="bg-white rounded-xl shadow-xl p-6 mb-6 border border-gray-100 v2-ai-inputs-slide-in">
                  <p className="text-lg font-bold text-gray-800 mb-4">AI Inputs</p>
                  {renderModelInput("Your decisions", activeInputs["Clinician Decisions"])}
                  {renderModelInput("Your notes", activeInputs["Clinician Notes"])}
                  {renderModelInput("Patient's report", activeInputs["Patient Prefill"])}
                  {renderModelInput("Informant comments", activeInputs["Informant Prefill"])}

                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleFocusModeApprove}
                      className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-500 font-semibold flex items-center gap-2 transition-colors"
                    >
                      <CheckIcon className="w-5 h-5" />
                      Approve
                      <kbd className="ml-1 px-1.5 py-0.5 bg-green-700/50 rounded text-xs">{modKey}+↵</kbd>
                    </button>
                    <button
                      onClick={exitFocusMode}
                      className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold border border-gray-300 transition-colors"
                    >
                      Cancel
                      <kbd className="ml-1 px-1.5 py-0.5 bg-gray-200 rounded text-xs">Esc</kbd>
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Layout activePage="dashboard" scrollable={false}>
      {showInitialWarning && (
        <InitialWarningDialog
          showInitialWarning={showInitialWarning}
          setShowInitialWarning={setShowInitialWarning}
        />
      )}

      <div className="h-full flex flex-col overflow-hidden relative">
        {/* Focus-mode overlay – covers the header, sidebar, and chrome with
            a dark scrim. pointer-events: none keeps the document area
            interactive. The document content area is elevated to z-50 so
            it sits above this overlay while the header dims behind it. */}
        <div
          className={`absolute inset-0 bg-black/60 transition-opacity duration-300 pointer-events-none
            ${focusMode ? 'opacity-100 z-40' : 'opacity-0 -z-10'}`}
        />

        {/* Header – stays in normal flow; the overlay dims it visually
            when focus mode is active. */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />

          {/* Compare with Claude – dev/staging UAT only */}
          {isCompareAvailable && (
            <CompareModeToolbar
              isLoading={isCompareLoading}
              hasComparison={hasComparison}
              showClaude={showClaude}
              sideBySide={sideBySide}
              progress={compareProgress}
              onGenerate={triggerComparison}
              onShowGemini={() => setViewMode('gemini')}
              onShowClaude={() => setViewMode('claude')}
              onToggleSideBySide={toggleSideBySide}
            />
          )}
        </div>

        {/* Scrollable document area – full width, centred with max-width.
            In focus mode this area is elevated above the overlay (z-50)
            while non-active paragraphs dim via opacity. */}
        <div className={`flex-1 px-8 pb-8 min-h-0 relative ${focusMode ? 'z-50' : ''}`}>
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              <div
                ref={documentContainerRef}
                className={`
                  ${sideBySide && hasComparison ? 'w-1/2' : 'mx-auto max-w-4xl'}
                  h-full overflow-y-auto p-16 m-4
                  transition-all duration-300
                  ${focusMode
                    ? 'bg-gray-100 border-gray-200 shadow-inner'
                    : 'bg-white shadow-md'
                  }
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section key={title} title={title} paragraphs={paragraphs} />
                  ))}
                </div>
                {/* Extra bottom padding so the last paragraph can scroll to
                    the top of the container during focus mode. */}
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />
              </div>
              {sideBySide && hasComparison && (
                <CompareClaudePanel
                  docStructure={docStructure}
                  claudeOutputs={claudeOutputs}
                  reportOutputs={REPORT_DATA_OUTPUTS}
                />
              )}
            </div>
          </div>
        </div>

        {/* Keyboard shortcut hint pill */}
        <KeyboardShortcutHint
          visible={Boolean(selectedCode)}
          inFocusMode={focusMode}
        />

        {/* Bulk approve modal */}
        {showBulkApproveModal && (
          <BulkApproveModal
            unapprovedCount={totalParagraphs - reviewedCodes.size}
            onApproveAll={handleBulkApprove}
            onGoBack={handleBulkGoBack}
            onSaveAndExit={handleExitReview}
          />
        )}
      </div>
    </Layout>
  );
}
